import React from 'react';
import { X, Eye } from 'lucide-react';
import { formatTextWithLinks } from '../../../../utils/formatters';

interface ViewLotModalProps {
  isOpen: boolean;
  onClose: () => void;
  lot: {
    lot_id: string;
    community_name: string | null;
    lot_perm_use: string | null;
    lot_zone: string | null;
    zone_desc?: string | null;
    lot_desc: string | null;
    leaselot_lease: number | null;
    leaselot_lot: string | null;
    leaselot_use: string | null;
    holder_name: string | null;
    lot_avail?: string;          // Y/N for availability
    lot_surr?: string;           // Y/N for surrendered
    lot_surr_note?: string | null; // Surrender notes
    lot_note?: string | null;    // General lot notes
  };
}

export default function ViewLotModal({ isOpen, onClose, lot }: ViewLotModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <Eye className="h-5 w-5 mr-2 text-primary" />
            <h3 className="text-lg font-medium">Lot Details</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Basic Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500">
                  Lot ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.lot_id}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Community
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.community_name ?? 'N/A'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Zone
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.zone_desc ?? lot.lot_zone ?? 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lot Available
                </label>
                <div className={`mt-1 p-2 border rounded-md text-sm font-semibold ${
                  lot.lot_avail === 'Y' || lot.lot_avail === 'y'
                    ? 'bg-green-50 text-green-700 border-green-200' 
                    : lot.lot_avail === 'N' || lot.lot_avail === 'n'
                    ? 'bg-red-50 text-red-700 border-red-200' 
                    : 'bg-gray-50 text-gray-700 border-gray-200'
                }`}>
                  {lot.lot_avail ? 
                    (lot.lot_avail.toUpperCase() === 'Y' ? 'Yes' : 
                     lot.lot_avail.toUpperCase() === 'N' ? 'No' : 
                     lot.lot_avail) 
                    : 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lot Surrendered
                </label>
                <div className={`mt-1 p-2 border rounded-md text-sm font-semibold ${
                  lot.lot_surr && lot.lot_surr.toUpperCase() === 'Y'
                    ? 'bg-green-50 text-green-700 border-green-200' 
                    : lot.lot_surr && lot.lot_surr.toUpperCase() === 'N'
                    ? 'bg-red-50 text-red-700 border-red-200' 
                    : 'bg-gray-50 text-gray-700 border-gray-200'
                }`}>
                  {lot.lot_surr ?
                    (lot.lot_surr.toUpperCase() === 'Y' ? 'Yes' : 
                     lot.lot_surr.toUpperCase() === 'N' ? 'No' : 
                     lot.lot_surr) 
                    : 'Not specified'}
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-xs text-gray-500">
                  Surrender Notes
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {formatTextWithLinks(lot.lot_surr_note ?? 'Not specified')}
                </div>
              </div>
            </div>
          </div>

          {/* Lease Details */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Lease Details</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500">
                  Lease ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.leaselot_lease ?? 'Not specified'}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Leaseholder
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {formatTextWithLinks(lot.holder_name ?? 'Not specified')}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">
                  Lease Lot ID
                </label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {lot.leaselot_lot ?? 'Not specified'}
                </div>
              </div>

            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Additional Information</h4>
            
            <div>
              <label className="block text-xs text-gray-500">
                Permitted Use
              </label>
              <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                {formatTextWithLinks(lot.lot_perm_use ?? 'Not specified')}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500">
                Description
              </label>
              <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                {formatTextWithLinks(lot.lot_desc ?? 'Not specified')}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500">
                Lot Notes
              </label>
              <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                {formatTextWithLinks(lot.lot_note ?? 'Not specified')}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
